import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  fetchTasks,
  fetchAppointments,
  tasksApi,
  appointmentsApi,
  type CrmTask,
  type Appointment,
  type TaskComment,
  type TaskChecklistItem,
  type TaskCreateRequest,
  type TaskUpdateRequest,
  type AppointmentCreateRequest,
  type AppointmentUpdateRequest,
} from '../api/operations'
// TASK-0167 (REQ-023-001): no operator/contest option list already loaded anywhere in this file
// (verified -- the Appointments section below has no operator-like selects either, just the
// static APPOINTMENT_TYPES vocabulary), so this reuses the existing dedicated fetchers already
// used by ProductsPage.tsx (`fetchOperators`) / ContestsPage.tsx (`contestsApi.list`) instead of
// introducing a new backend endpoint.
import { fetchOperators, type Operator } from '../api/catalog'
import { contestsApi, type Contest } from '../api/performance'
// TASK-0195 (REQ-023-002): "Allegati" section of the edit-task modal -- reuses `documentsApi`
// exactly as-is (entityType='Task'), already fully generic per DocumentsController.cs (verified by
// TASK-0192/TASK-0128: only "Contract" gets special ADR-008 handling, every other EntityType string
// passes through unrestricted).
import { documentsApi, type DocumentSummary } from '../api/documents'
import { SortKeyTh, SortResetLink } from '../components/SortKeyTh'
import {
  Button,
  Callout,
  Checkbox,
  Card,
  DataTable,
  Dialog,
  EmptyState,
  FormGrid,
  FormSection,
  IconButton,
  List,
  ListItem,
  LoadingState,
  Page,
  PageHeader,
  Select,
  SegmentedButton,
  StatusPill,
  Switch,
  Tabs,
  TextField,
  useConfirm,
  useSnackbar,
  type PillTone,
  usePrompt,
} from '../components/ui'

/** F-101: ordinamenti di default di GET /api/tasks e GET /api/appointments. */
const TASKS_DEFAULT_SORT = 'created_desc'
const APPTS_DEFAULT_SORT = 'start_asc'

type ActiveTab = 'tasks' | 'appointments'

const PAGE_SIZE = 20

/** I colori di stato restano nomi Bootstrap nelle tabelle sotto: qui diventano il tono della StatusPill. */
function pillTone(color: string): PillTone {
  switch (color) {
    case 'danger': return 'error'
    case 'warning': return 'warning'
    case 'info': return 'info'
    case 'success': return 'success'
    case 'primary': return 'primary'
    default: return 'neutral'
  }
}

const taskPriorityColors: Record<string, string> = {
  urgent: 'danger',
  high: 'warning',
  normal: 'info',
  low: 'secondary',
}

const taskPriorityLabels: Record<string, string> = {
  urgent: 'Urgente',
  high: 'Alta',
  normal: 'Normale',
  low: 'Bassa',
}

const taskStatusColors: Record<string, string> = {
  pending: 'warning',
  in_progress: 'primary',
  completed: 'success',
  cancelled: 'secondary',
}

const taskStatusLabels: Record<string, string> = {
  pending: 'Pendente',
  in_progress: 'In Corso',
  completed: 'Completato',
  cancelled: 'Annullato',
}

// TASK-0226 (REQ-023-003): the 4 real `Task.status` values, in the same order as
// `taskStatusLabels`/`taskStatusColors` above -- drives the Kanban view's column order. Kept as
// an explicit array (not `Object.keys(taskStatusLabels)`) so the order is guaranteed regardless
// of how those two lookup objects are declared.
const TASK_STATUSES = ['pending', 'in_progress', 'completed', 'cancelled'] as const

// TASK-0251 (REQ-080-001): the 4 remaining entries -- `to_confirm`/`cancelled_by_customer`/
// `cancelled_by_agent`/`rescheduled`/`to_recall` -- complete the requirement's 9-state vocabulary,
// same "already defined, waiting for a backend producer" pattern TASK-0188 used for
// `confirmed`/`no_show` above.
const apptStatusColors: Record<string, string> = {
  scheduled: 'primary',
  confirmed: 'success',
  completed: 'info',
  cancelled: 'danger',
  no_show: 'warning',
  to_confirm: 'secondary',
  cancelled_by_customer: 'danger',
  cancelled_by_agent: 'dark',
  rescheduled: 'info',
  to_recall: 'warning',
}

const apptStatusLabels: Record<string, string> = {
  scheduled: 'Pianificato',
  confirmed: 'Confermato',
  completed: 'Completato',
  cancelled: 'Annullato',
  no_show: 'Non Presentato',
  to_confirm: 'Da confermare',
  cancelled_by_customer: 'Annullato cliente',
  cancelled_by_agent: 'Annullato agente',
  rescheduled: 'Rinviato',
  to_recall: 'Da richiamare',
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  })
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// TASK-0076: inverse of the `new Date(apptForm.startAt).toISOString()` conversion the create
// form already uses (`handleCreateAppt` below) -- turns an ISO UTC timestamp from
// `appointmentsApi.get()` back into the local-time `YYYY-MM-DDTHH:mm` string a
// `datetime-local` input expects. `Date`'s `get*` accessors (as opposed to `getUTC*`) already
// return local-time components, so this is a plain reformat, not a timezone conversion --
// round-tripping through `new Date(value).toISOString()` on save reproduces the original
// instant.
function toDatetimeLocalValue(iso: string): string {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

// REQ-084-001: Monday-first start-of-week for a date, matching the Monday-first weekday
// ordering the Month grid already uses (`weekdayLabels` below) -- used only by the Week format's
// column layout, never by List/Month.
function startOfWeekMonday(d: Date): Date {
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate())
  const mondayOffset = (start.getDay() + 6) % 7 // getDay(): 0=Sunday..6=Saturday
  start.setDate(start.getDate() - mondayOffset)
  return start
}

function isOverdue(dueDate: string | null, status: string): boolean {
  return (
    dueDate !== null &&
    dueDate !== undefined &&
    new Date(dueDate) < new Date() &&
    !['completed', 'cancelled'].includes(status)
  )
}

// ─── Tasks section ────────────────────────────────────────────────────────────

interface TaskForm {
  title: string
  description: string
  priority: string
  dueDate: string
  /** TASK-0167 (REQ-023-001): unlike `contractId`/`leadId`/`customerId` (edit-form-only, see
   * `TaskEditForm`'s doc comment below), `operatorId`/`contestId` ARE offered on the create form
   * too -- both selectors are backed by real reference data already fetched for this section
   * (`operators`/`contests` state below), unlike the raw-ID contract/lead/customer links which
   * have no name-resolution picker anywhere in this app. Empty string means "none selected". */
  operatorId: string
  contestId: string
}

const defaultTaskForm: TaskForm = {
  title: '', description: '', priority: 'normal', dueDate: '', operatorId: '', contestId: '',
}

/**
 * TASK-0075: edit form -- deliberately carries `contractId`/`leadId`/`customerId` (as raw-ID
 * text, same "#<id>" style already used for `assignedToId` in the table and for `customerId`
 * in `ContractFormPage.tsx`'s "ID Cliente" input -- no name-resolution picker exists anywhere
 * in this section, so this doesn't invent a new pattern) even though the create form/modal
 * above never asks for them. They must round-trip through the edit form unresolved, because
 * `CrmTask.UpdateDetails` overwrites `ContractId`/`LeadId`/`CustomerId` unconditionally on every
 * save (see `handleSaveEdit` below) -- a task created via `LeadDetailPage.tsx`'s "task da questo
 * lead" flow (which sets `leadId`) would silently lose that link the first time anyone edited
 * just its title, unless the edit form always has the current value on hand to send back.
 *
 * TASK-0167 (REQ-023-001): `operatorId`/`contestId` join this form for the same full-replace
 * reason -- `UpdateDetails`'s new trailing parameters overwrite them unconditionally too, so the
 * edit form must always carry the task's current value even though these are select-driven
 * (not raw-ID text like the other three).
 */
interface TaskEditForm {
  title: string
  description: string
  priority: string
  dueDate: string
  assignedToId: string
  contractId: string
  leadId: string
  customerId: string
  operatorId: string
  contestId: string
}

const defaultTaskEditForm: TaskEditForm = {
  title: '', description: '', priority: 'normal', dueDate: '',
  assignedToId: '', contractId: '', leadId: '', customerId: '', operatorId: '', contestId: '',
}

function TasksSection() {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const prompt = usePrompt()
  const [tasks, setTasks] = useState<CrmTask[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState('')
  // F-101: priorità e ordinamento sono lato server (prima la priorità filtrava la sola pagina aperta).
  const [priorityFilter, setPriorityFilter] = useState('')
  const [sort, setSort] = useState(TASKS_DEFAULT_SORT)
  // TASK-0187 (REQ-023-003): "Scadenza" filter -- server-side, same wiring as `statusFilter`
  // (sent to `fetchTasks`, resets to page 1 on change), NOT client-side like `priorityFilter`
  // below, because `dueFilter` needs `TasksController.List`'s own DueDate comparison to be
  // correct across pages (client-side filtering of only the current page would silently miss
  // matching rows on other pages).
  const [dueFilter, setDueFilter] = useState('')
  // TASK-0196 (REQ-023-003): "Team Tasks" toggle -- server-side, wired exactly like `dueFilter`
  // above (sent to `fetchTasks`, resets page to 1 on change), NOT a "My Tasks" toggle (that is
  // already achievable via the existing `assignedToId` param and is out of this task's scope).
  const [teamOnly, setTeamOnly] = useState(false)
  // TASK-0226 (REQ-023-003): purely client-side view-mode toggle for the section below (List/
  // Kanban/Calendar) -- default 'list' so nobody who never touches the toggle sees any behavior
  // change. Unlike `statusFilter`/`dueFilter`/`teamOnly` above, this never touches
  // `load()`/`fetchTasks`: it only changes which JSX branch renders `filteredTasks`.
  // TASK-0227 (REQ-023-003): extended with 'calendar' -- the 7th and last Task view from
  // REQ-023-003, read-only monthly grid, deliberately separate scope from Kanban.
  const [taskViewMode, setTaskViewMode] = useState<'list' | 'kanban' | 'calendar'>('list')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [showTaskModal, setShowTaskModal] = useState(false)
  const [taskForm, setTaskForm] = useState<TaskForm>(defaultTaskForm)
  const [savingTask, setSavingTask] = useState(false)

  const [showEditModal, setShowEditModal] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState<TaskEditForm>(defaultTaskEditForm)
  const [loadingEdit, setLoadingEdit] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  // TASK-0186 (REQ-023-002): "Commenti" section of the edit-task modal -- lazy-loaded when the
  // modal opens, same pattern `openEdit` already uses to fetch the task detail itself.
  const [comments, setComments] = useState<TaskComment[]>([])
  const [loadingComments, setLoadingComments] = useState(false)
  const [commentText, setCommentText] = useState('')
  const [addingComment, setAddingComment] = useState(false)
  const [commentError, setCommentError] = useState<string | null>(null)

  // TASK-0219 (REQ-023-002): "Checklist" section of the edit-task modal -- same lazy-load-on-open
  // pattern as "Commenti" above (its own `openEdit`/`closeEditModal` isolation principle applies
  // unchanged here: a failure loading the checklist does not block the rest of the modal). Unlike
  // `comments`, this list is also mutated in place after load (toggle/delete), not only appended to.
  const [checklist, setChecklist] = useState<TaskChecklistItem[]>([])
  const [loadingChecklist, setLoadingChecklist] = useState(false)
  const [checklistText, setChecklistText] = useState('')
  const [addingChecklistItem, setAddingChecklistItem] = useState(false)
  const [checklistError, setChecklistError] = useState<string | null>(null)

  // TASK-0195 (REQ-023-002): "Allegati" section of the edit-task modal -- same lazy-load-on-open
  // pattern as "Commenti" above, but backed by the already-generic `documentsApi`
  // (entityType='Task') instead of a dedicated TaskComment endpoint. No separate text field is
  // needed for upload (unlike `commentText`) -- the file input itself triggers the upload.
  const [attachments, setAttachments] = useState<DocumentSummary[]>([])
  const [loadingAttachments, setLoadingAttachments] = useState(false)
  const [attachmentError, setAttachmentError] = useState<string | null>(null)
  const [uploadingAttachment, setUploadingAttachment] = useState(false)

  // TASK-0167 (REQ-023-001): operators/contests for the task form's "Operatore"/"Gara"
  // selectors -- small, mostly-static reference data, fetched once, same fetch-once pattern as
  // `operators`/`categories` in ProductsPage.tsx (TASK-0077).
  const [operators, setOperators] = useState<Operator[]>([])
  const [contests, setContests] = useState<Contest[]>([])

  useEffect(() => {
    fetchOperators().then(res => setOperators(res.items)).catch(() => setOperators([]))
    contestsApi.list().then(res => setContests(res.items)).catch(() => setContests([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params: { page: number; status?: string; dueFilter?: string; teamOnly?: boolean; sort: string; priority?: string } = { page, sort }
      if (statusFilter) params.status = statusFilter
      if (priorityFilter) params.priority = priorityFilter
      if (dueFilter) params.dueFilter = dueFilter
      if (teamOnly) params.teamOnly = teamOnly
      const result = await fetchTasks(params)
      setTasks(result.items)
      setTotal(result.total)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [page, statusFilter, priorityFilter, dueFilter, teamOnly, sort])

  useEffect(() => { void load() }, [load])

  function handleSort(next: string) { setSort(next); setPage(1) }

  const filteredTasks = tasks

  const totalPages = Math.ceil(total / PAGE_SIZE)

  async function handleCreateTask() {
    if (!taskForm.title.trim()) return
    setSavingTask(true)
    try {
      const req: TaskCreateRequest = { title: taskForm.title.trim() }
      if (taskForm.description.trim()) req.description = taskForm.description.trim()
      if (taskForm.priority) req.priority = taskForm.priority
      if (taskForm.dueDate) req.dueDate = new Date(taskForm.dueDate).toISOString()
      if (taskForm.operatorId) req.operatorId = Number(taskForm.operatorId)
      if (taskForm.contestId) req.contestId = Number(taskForm.contestId)
      await tasksApi.create(req)
      setShowTaskModal(false)
      setTaskForm(defaultTaskForm)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    } finally {
      setSavingTask(false)
    }
  }

  // TASK-0075: opens the edit modal pre-filled from `GET /api/tasks/{id}` (`tasksApi.get`),
  // NOT from the `task` row already in `filteredTasks`. The list row is missing `description`
  // (`TasksController.List`'s projection never selects it -- see `CrmTaskDetail`'s doc comment
  // in api/operations.ts), so building the form from the row would silently blank the
  // description of every task edited from this table.
  async function openEdit(id: number) {
    setEditId(id)
    setEditError(null)
    setShowEditModal(true)
    setLoadingEdit(true)
    try {
      const detail = await tasksApi.get(id)
      setEditForm({
        title: detail.title,
        description: detail.description ?? '',
        priority: detail.priority ?? 'normal',
        dueDate: detail.dueDate ? detail.dueDate.slice(0, 10) : '',
        assignedToId: detail.assignedToId != null ? String(detail.assignedToId) : '',
        contractId: detail.contractId != null ? String(detail.contractId) : '',
        leadId: detail.leadId != null ? String(detail.leadId) : '',
        customerId: detail.customerId != null ? String(detail.customerId) : '',
        operatorId: detail.operatorId != null ? String(detail.operatorId) : '',
        contestId: detail.contestId != null ? String(detail.contestId) : '',
      })
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : 'Errore nel caricamento del task')
    } finally {
      setLoadingEdit(false)
    }

    // TASK-0186 (REQ-023-002): loaded independently of the form fields above -- a failure here
    // (e.g. the task became invisible between the row being rendered and this click) surfaces in
    // the "Commenti" section alone and does not block the rest of the modal.
    setLoadingComments(true)
    setCommentError(null)
    try {
      const list = await tasksApi.getComments(id)
      setComments(list)
    } catch (e: unknown) {
      setCommentError(e instanceof Error ? e.message : 'Errore nel caricamento dei commenti')
    } finally {
      setLoadingComments(false)
    }

    // TASK-0219 (REQ-023-002): loaded independently of the form fields/comments above -- same
    // isolation principle as Commenti: a failure here surfaces in the "Checklist" section alone
    // and does not block the rest of the modal.
    setLoadingChecklist(true)
    setChecklistError(null)
    try {
      const list = await tasksApi.getChecklist(id)
      setChecklist(list)
    } catch (e: unknown) {
      setChecklistError(e instanceof Error ? e.message : 'Errore nel caricamento della checklist')
    } finally {
      setLoadingChecklist(false)
    }

    // TASK-0195 (REQ-023-002): loaded independently of both the form fields and the comments
    // above -- same isolation principle as Commenti: a failure here surfaces in the "Allegati"
    // section alone and does not block the rest of the modal.
    setLoadingAttachments(true)
    setAttachmentError(null)
    try {
      const list = await documentsApi.list('Task', id)
      setAttachments(list)
    } catch (e: unknown) {
      setAttachmentError(e instanceof Error ? e.message : 'Errore nel caricamento degli allegati')
    } finally {
      setLoadingAttachments(false)
    }
  }

  function closeEditModal() {
    setShowEditModal(false)
    setEditId(null)
    setEditForm(defaultTaskEditForm)
    setEditError(null)
    setComments([])
    setCommentText('')
    setCommentError(null)
    setChecklist([])
    setChecklistText('')
    setChecklistError(null)
    setAttachments([])
    setAttachmentError(null)
  }

  // TASK-0186 (REQ-023-002): append-only -- no edit/delete of existing comments, same principle as
  // the backend's TaskComment.
  async function handleAddComment() {
    if (editId === null || !commentText.trim()) return
    setAddingComment(true)
    setCommentError(null)
    try {
      const c = await tasksApi.addComment(editId, commentText.trim())
      setComments(prev => [...prev, c])
      setCommentText('')
    } catch (e: unknown) {
      setCommentError(e instanceof Error ? e.message : "Errore nell'aggiunta del commento")
    } finally {
      setAddingComment(false)
    }
  }

  // TASK-0219 (REQ-023-002): unlike `handleAddComment` above, this list is mutable -- `toggle`/
  // `remove` below reuse `checklistText`'s sibling state the same way, but this one only appends.
  async function handleAddChecklistItem() {
    if (editId === null || !checklistText.trim()) return
    setAddingChecklistItem(true)
    setChecklistError(null)
    try {
      const item = await tasksApi.addChecklistItem(editId, checklistText.trim())
      setChecklist(prev => [...prev, item])
      setChecklistText('')
    } catch (e: unknown) {
      setChecklistError(e instanceof Error ? e.message : "Errore nell'aggiunta della voce")
    } finally {
      setAddingChecklistItem(false)
    }
  }

  // TASK-0219 (REQ-023-002): optimistic local update + rollback on error -- the local `checklist`
  // state flips immediately (so the checkbox feels instant, without waiting on the round trip), and
  // is reverted (flipped back to `!isDone`) if `setChecklistItemDone` fails, so a failed PATCH never
  // leaves the UI showing a state the server didn't actually save.
  async function handleToggleChecklistItem(itemId: number, isDone: boolean) {
    if (editId === null) return
    setChecklistError(null)
    setChecklist(prev => prev.map(c => (c.id === itemId ? { ...c, isDone } : c)))
    try {
      await tasksApi.setChecklistItemDone(editId, itemId, isDone)
    } catch (e: unknown) {
      setChecklist(prev => prev.map(c => (c.id === itemId ? { ...c, isDone: !isDone } : c)))
      setChecklistError(e instanceof Error ? e.message : 'Errore nel salvataggio della checklist')
    }
  }

  // TASK-0219 (REQ-023-002): conferma modale, same minimal-confirmation pattern
  // `handleDelete`/`handleCancel` already use elsewhere in this file for a destructive action.
  async function handleDeleteChecklistItem(itemId: number) {
    if (editId === null) return
    const ok = await confirm({
      title: 'Eliminare la voce',
      message: 'Eliminare questa voce della checklist?',
      confirmLabel: 'Elimina',
      destructive: true,
    })
    if (!ok) return
    const prevChecklist = checklist
    setChecklistError(null)
    setChecklist(prev => prev.filter(c => c.id !== itemId))
    try {
      await tasksApi.deleteChecklistItem(editId, itemId)
    } catch (e: unknown) {
      setChecklist(prevChecklist)
      setChecklistError(e instanceof Error ? e.message : "Errore nell'eliminazione della voce")
    }
  }

  // TASK-0195 (REQ-023-002): fires immediately on file selection (no separate confirm step,
  // unlike `handleAddComment`'s explicit "Aggiungi" button) -- same minimal upload-on-select
  // pattern as the hidden file input in DocumentsPage.tsx. Re-lists after upload so the new
  // attachment appears without requiring the modal to be reopened.
  async function handleUploadAttachment(file: File) {
    if (editId === null) return
    setUploadingAttachment(true)
    setAttachmentError(null)
    try {
      await documentsApi.upload('Task', editId, file)
      const list = await documentsApi.list('Task', editId)
      setAttachments(list)
    } catch (e: unknown) {
      setAttachmentError(e instanceof Error ? e.message : "Errore nel caricamento dell'allegato")
    } finally {
      setUploadingAttachment(false)
    }
  }

  // TASK-0075: `description`/`dueDate`/`contractId`/`leadId`/`customerId` are ALWAYS included
  // below (never conditionally, unlike `handleCreateTask` above) because `CrmTask.UpdateDetails`
  // overwrites them unconditionally server-side, even with null -- see `TaskUpdateRequest`'s doc
  // comment in api/operations.ts. `dueDate` reuses the exact same
  // `new Date(...).toISOString()` conversion as `handleCreateTask` (avoids the Kind=Unspecified
  // crash class already fixed elsewhere this session).
  async function handleSaveEdit() {
    if (editId === null || !editForm.title.trim()) return
    setSavingEdit(true)
    setEditError(null)
    try {
      // `editForm.priority` is always one of the <select>'s 4 non-empty options (defaults to
      // 'normal'), so it's always sent as a real value -- never `undefined` -- unlike
      // `assignedToId` below, which stays genuinely optional (empty input = untouched).
      const req: TaskUpdateRequest = {
        title: editForm.title.trim(),
        description: editForm.description.trim() ? editForm.description.trim() : null,
        priority: editForm.priority,
        dueDate: editForm.dueDate ? new Date(editForm.dueDate).toISOString() : null,
        contractId: editForm.contractId.trim() ? Number(editForm.contractId.trim()) : null,
        leadId: editForm.leadId.trim() ? Number(editForm.leadId.trim()) : null,
        customerId: editForm.customerId.trim() ? Number(editForm.customerId.trim()) : null,
        operatorId: editForm.operatorId.trim() ? Number(editForm.operatorId.trim()) : null,
        contestId: editForm.contestId.trim() ? Number(editForm.contestId.trim()) : null,
      }
      if (editForm.assignedToId.trim()) req.assignedToId = Number(editForm.assignedToId.trim())
      await tasksApi.update(editId, req)
      closeEditModal()
      void load()
    } catch (e: unknown) {
      setEditError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSavingEdit(false)
    }
  }

  async function handleComplete(id: number) {
    try {
      await tasksApi.complete(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  async function handleDelete(id: number) {
    // F-104 (REQ-287-001): non si cancella, si sposta nel cestino (si ripristina da Impostazioni > Cestino).
    const reason = await prompt({
      title: 'Sposta il task nel cestino',
      message: 'Spostare questo task nel cestino? Motivo (facoltativo, max 500 caratteri).',
      label: 'Motivo (facoltativo)',
      initialValue: '',
      confirmLabel: 'Sposta nel cestino',
      destructive: true,
    })
    if (reason === null) return
    try {
      await tasksApi.delete(id, reason.slice(0, 500))
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  return (
    <Page>
      {/* Filter row */}
      <div className="d-flex gap-2 align-items-center justify-content-between mb-3 flex-wrap">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <Select
            label="Tutti gli stati"
            labelHidden
            className="w-auto"
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
          >
            <option value="">Tutti gli stati</option>
            <option value="pending">Pendenti</option>
            <option value="in_progress">In corso</option>
            <option value="completed">Completati</option>
          </Select>
          <Select
            label="Tutte le priorità"
            labelHidden
            className="w-auto"
            value={priorityFilter}
            onChange={e => { setPriorityFilter(e.target.value); setPage(1) }}
          >
            <option value="">Tutte le priorità</option>
            <option value="urgent">Urgente</option>
            <option value="high">Alta</option>
            <option value="normal">Normale</option>
            <option value="low">Bassa</option>
          </Select>
          {/* TASK-0187 (REQ-023-003): server-side, wired like statusFilter above (resets page to
              1 on change) -- not client-side like priorityFilter, see dueFilter's own comment. */}
          <Select
            label="Tutte le scadenze"
            labelHidden
            className="w-auto"
            value={dueFilter}
            onChange={e => { setDueFilter(e.target.value); setPage(1) }}
          >
            <option value="">Tutte le scadenze</option>
            <option value="overdue">In ritardo</option>
            <option value="today">Oggi</option>
            <option value="upcoming">Prossimi 7 giorni</option>
          </Select>
          {/* TASK-0196 (REQ-023-003): "Team Tasks" toggle -- server-side, wired like dueFilter
              above (resets page to 1 on change). NOT a "My Tasks" toggle -- see teamOnly's own
              comment for why that is out of scope here. */}
          <Switch
            id="teamOnlyToggle"
            label="Solo il mio team"
            checked={teamOnly}
            onChange={e => { setTeamOnly(e.target.checked); setPage(1) }}
          />
          <SortResetLink sort={sort} defaultSort={TASKS_DEFAULT_SORT} onReset={() => handleSort(TASKS_DEFAULT_SORT)} />
          {/* TASK-0226 (REQ-023-003): List/Kanban view toggle -- purely client-side, does not
              touch any filter state or trigger `load()`. */}
          <SegmentedButton
            aria-label="Modalità di visualizzazione"
            options={[{ value: 'list', label: 'Lista' }, { value: 'kanban', label: 'Kanban' }, { value: 'calendar', label: 'Calendario' }]}
            value={taskViewMode}
            onChange={v => setTaskViewMode(v as 'list' | 'kanban' | 'calendar')}
          />
        </div>
        <Button variant="filled" size="sm" icon="add"
          onClick={() => setShowTaskModal(true)}>
          Nuovo task
        </Button>
      </div>

      {error && <Callout tone="error">{error}</Callout>}

      {/* Table card */}
      <Card padding="none">
          {loading ? (
            <LoadingState />
          ) : filteredTasks.length === 0 ? (
            <EmptyState icon="check_box" title="Nessun task" />
          ) : taskViewMode === 'kanban' ? (
            // TASK-0226 (REQ-023-003): Kanban view -- same `filteredTasks` array as the table
            // below, just grouped client-side by `task.status` into 4 columns instead of rows.
            // Reuses every helper/lookup the table branch already uses (openEdit/handleComplete/
            // handleDelete/isOverdue/formatDate/taskPriorityColors/taskPriorityLabels/
            // taskStatusColors/taskStatusLabels) -- no new logic, just a different layout.
            <div className="p-3 overflow-x-auto">
              <div className="d-flex gap-3 align-items-start">
                {TASK_STATUSES.map(status => {
                  const columnTasks = filteredTasks.filter(t => t.status === status)
                  return (
                    <div key={status} className="flex-shrink-0 md-u-w-280">
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <StatusPill tone={pillTone(taskStatusColors[status] ?? 'secondary')}>
                          {taskStatusLabels[status]}
                        </StatusPill>
                        <span className="text-muted md-typescale-body-small">{columnTasks.length}</span>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {columnTasks.length === 0 ? (
                          <div className="text-muted md-typescale-body-small">Nessun task</div>
                        ) : (
                          columnTasks.map(task => {
                            const overdue = isOverdue(task.dueDate, task.status)
                            const priorityColor = taskPriorityColors[task.priority ?? ''] ?? 'secondary'
                            const priorityLabel = taskPriorityLabels[task.priority ?? ''] ?? (task.priority ?? '—')
                            return (
                              <Card key={task.id} padding="sm">
                                  <div className="mb-1 md-typescale-emphasized-body-small">{task.title}</div>
                                  <div className="d-flex align-items-center justify-content-between mb-2">
                                    {task.priority
                                      ? <StatusPill tone={pillTone(priorityColor)}>{priorityLabel}</StatusPill>
                                      : <span className="text-muted md-typescale-body-small">—</span>
                                    }
                                    {task.dueDate
                                      ? (
                                        <span className={`${overdue ? 'text-danger md-typescale-emphasized-body-small' : 'md-typescale-body-small'}`}>
                                          {formatDate(task.dueDate)}
                                        </span>
                                      )
                                      : <span className="text-muted md-typescale-body-small">—</span>
                                    }
                                  </div>
                                  <div className="text-end">
                                    <IconButton size="sm" icon="edit" aria-label="Modifica" onClick={() => void openEdit(task.id)} />
                                    {task.status !== 'completed' && (
                                      <IconButton size="sm" icon="done" aria-label="Completa" onClick={() => void handleComplete(task.id)} />
                                    )}
                                    <IconButton size="sm" icon="delete" aria-label="Sposta nel cestino" onClick={() => void handleDelete(task.id)} />
                                  </div>
                              </Card>
                            )
                          })
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          ) : taskViewMode === 'calendar' ? (
            // TASK-0227 (REQ-023-003): Calendar view -- read-only monthly grid for the current
            // month/year only (`new Date()` at render time, no new state -- deliberately no
            // month-forward/back navigation, out of scope per the task card). Same `filteredTasks`
            // array as List/Kanban, grouped client-side by local year/month/day of `dueDate`.
            // Tasks with `dueDate === null` or in a different month are, by construction, not
            // shown anywhere in this branch (no "no due date" section -- List/Kanban remain the
            // way to see those). Reuses `taskPriorityColors`/`openEdit` -- no new lookups/actions.
            (() => {
              const today = new Date()
              const year = today.getFullYear()
              const month = today.getMonth()
              const daysInMonth = new Date(year, month + 1, 0).getDate()
              // getDay(): 0=Sunday..6=Saturday -- shifted to a Monday-first week (it-IT
              // convention); purely a presentation choice, not mandated by the task card.
              const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7
              const cells: Array<number | null> = [
                ...Array.from({ length: firstWeekday }, () => null),
                ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
              ]
              while (cells.length % 7 !== 0) cells.push(null)
              const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
              return (
                <div className="p-3">
                  <div className="mb-2 md-typescale-emphasized-body-medium">
                    {today.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className="md-u-scroll-x">
                    <div className="d-grid md-u-grid-7">
                      {weekdayLabels.map(label => (
                        <div key={label} className="text-center text-muted md-typescale-emphasized-body-small">
                          {label}
                        </div>
                      ))}
                      {cells.map((day, idx) => {
                        if (day === null) {
                          return <div key={`empty-${idx}`} className="md-u-minh-88" />
                        }
                        const dayTasks = filteredTasks.filter(t => {
                          if (!t.dueDate) return false
                          const d = new Date(t.dueDate)
                          return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day
                        })
                        return (
                          <Card as="div" variant="outlined" padding="sm" key={day} className="gap-1 md-u-minh-88">
                            <div className="md-typescale-body-small text-muted mb-1">{day}</div>
                            <div className="d-flex flex-column gap-1">
                              {dayTasks.map(task => {
                                const priorityColor = taskPriorityColors[task.priority ?? ''] ?? 'secondary'
                                return (
                                  <button
                                    key={task.id}
                                    type="button"
                                    title={task.title}
                                    className="border-0 bg-transparent p-0 text-start w-100"
                                    onClick={() => void openEdit(task.id)}
                                    >
                                      <StatusPill tone={pillTone(priorityColor)} className="text-truncate">{task.title}</StatusPill>
                                    </button>
                                )
                              })}
                            </div>
                          </Card>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })()
          ) : (
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>Assegnato</th>
                    <SortKeyTh label="Priorità" ascKey="priority" sort={sort} onSort={handleSort} />
                    <SortKeyTh label="Scadenza" ascKey="due_asc" descKey="due_desc" sort={sort} onSort={handleSort} />
                    <SortKeyTh label="Stato" ascKey="status" sort={sort} onSort={handleSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTasks.map(task => {
                    const overdue = isOverdue(task.dueDate, task.status)
                    const priorityColor = taskPriorityColors[task.priority ?? ''] ?? 'secondary'
                    const priorityLabel = taskPriorityLabels[task.priority ?? ''] ?? (task.priority ?? '—')
                    const statusColor = taskStatusColors[task.status] ?? 'secondary'
                    const statusLabel = taskStatusLabels[task.status] ?? task.status
                    return (
                      <tr key={task.id}>
                        <td>
                          <strong className="md-typescale-body-small">{task.title}</strong>
                        </td>
                        <td>
                          <small>{task.assignedToId ?? '-'}</small>
                        </td>
                        <td>
                          {task.priority
                            ? <StatusPill tone={pillTone(priorityColor)}>{priorityLabel}</StatusPill>
                            : <span className="text-muted md-typescale-body-small">—</span>
                          }
                        </td>
                        <td>
                          {task.dueDate
                            ? (
                              <span className={overdue ? 'text-danger md-typescale-emphasized-body-small' : ''}>
                                {formatDate(task.dueDate)}
                              </span>
                            )
                            : <span className="text-muted md-typescale-body-small">—</span>
                          }
                        </td>
                        <td>
                          <StatusPill tone={pillTone(statusColor)}>{statusLabel}</StatusPill>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end flex-wrap gap-1">
                          <IconButton size="sm" icon="edit" aria-label="Modifica" onClick={() => void openEdit(task.id)} />
                          {task.status !== 'completed' && (
                            <IconButton size="sm" icon="done" aria-label="Completa" onClick={() => void handleComplete(task.id)} />
                          )}
                          <IconButton size="sm" icon="delete" aria-label="Sposta nel cestino" onClick={() => void handleDelete(task.id)} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
          )}
      </Card>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center md-typescale-body-small text-muted">
        <span>{total.toLocaleString('it-IT')} totali</span>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outlined" size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}>
            ‹ Precedente
          </Button>
          <span>Pagina {page}{totalPages > 0 ? ` di ${totalPages}` : ''}</span>
          <Button variant="outlined" size="sm"
            disabled={tasks.length < PAGE_SIZE}
            onClick={() => setPage(p => p + 1)}>
            Successiva ›
          </Button>
        </div>
      </div>

      {/* New Task Modal */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleCreateTask() }}
        open={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        title="Nuovo task"
        actions={
          <>
            <Button variant="text"
              onClick={() => setShowTaskModal(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="text"
              disabled={!taskForm.title.trim() || savingTask}
              >
              {savingTask ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
          <FormGrid>
            <TextField className="md-span-full" label="Titolo *"
              type="text"
              required
              value={taskForm.title}
              onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))}
            />
            <Select label="Priorità"
              value={taskForm.priority}
              onChange={e => setTaskForm(f => ({ ...f, priority: e.target.value }))}
            >
              <option value="normal">Normale</option>
              <option value="low">Bassa</option>
              <option value="high">Alta</option>
              <option value="urgent">Urgente</option>
            </Select>
            <TextField label="Scadenza"
              type="date"
              value={taskForm.dueDate}
              onChange={e => setTaskForm(f => ({ ...f, dueDate: e.target.value }))}
            />
            {/* TASK-0167 (REQ-023-001): Operatore/Gara -- see TaskForm's doc comment above. */}
            <Select label="Operatore"
              value={taskForm.operatorId}
              onChange={e => setTaskForm(f => ({ ...f, operatorId: e.target.value }))}
            >
              <option value="">Nessuno</option>
              {operators.map(o => (
                <option key={o.id} value={String(o.id)}>{o.name}</option>
              ))}
            </Select>
            <Select label="Gara"
              value={taskForm.contestId}
              onChange={e => setTaskForm(f => ({ ...f, contestId: e.target.value }))}
            >
              <option value="">Nessuna</option>
              {contests.map(c => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </Select>
            <TextField className="md-span-full" multiline label="Descrizione"
              rows={2}
              value={taskForm.description}
              onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))}
            />
          </FormGrid>
      </Dialog>

      {/* Edit Task Modal (TASK-0075) */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleSaveEdit() }}
        open={showEditModal}
        onClose={closeEditModal}
        title="Modifica task"
        actions={
          <>
            <Button variant="text" onClick={closeEditModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text"
              disabled={loadingEdit || !editForm.title.trim() || savingEdit}
              >
              {savingEdit ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
          {loadingEdit ? (
            <LoadingState />
          ) : (
            <>
              {editError && <Callout tone="error">{editError}</Callout>}
              <FormSection title="Task">
                <TextField className="md-span-full" label="Titolo *"
                  type="text"
                  required
                  value={editForm.title}
                  onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))}
                />
                <Select label="Priorità"
                  value={editForm.priority}
                  onChange={e => setEditForm(f => ({ ...f, priority: e.target.value }))}
                >
                  <option value="normal">Normale</option>
                  <option value="low">Bassa</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </Select>
                <TextField label="Scadenza"
                  type="date"
                  value={editForm.dueDate}
                  onChange={e => setEditForm(f => ({ ...f, dueDate: e.target.value }))}
                />
                <TextField className="md-span-full" multiline label="Descrizione"
                  rows={2}
                  value={editForm.description}
                  onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                />
              </FormSection>
              {/* TASK-0167 (REQ-023-001): Operatore/Gara -- see TaskEditForm's doc comment
                  above for why these must always round-trip through the form (full-replace,
                  same as ID Contratto/ID Lead/ID Cliente above). */}
              <FormSection title="Collegamenti">
                <TextField label="ID Assegnatario"
                  type="number"
                  value={editForm.assignedToId}
                  onChange={e => setEditForm(f => ({ ...f, assignedToId: e.target.value }))}
                />
                <TextField label="ID Contratto"
                  type="number"
                  value={editForm.contractId}
                  onChange={e => setEditForm(f => ({ ...f, contractId: e.target.value }))}
                />
                <TextField label="ID Lead"
                  type="number"
                  value={editForm.leadId}
                  onChange={e => setEditForm(f => ({ ...f, leadId: e.target.value }))}
                />
                <TextField label="ID Cliente"
                  type="number"
                  value={editForm.customerId}
                  onChange={e => setEditForm(f => ({ ...f, customerId: e.target.value }))}
                />
                <Select label="Operatore"
                  value={editForm.operatorId}
                  onChange={e => setEditForm(f => ({ ...f, operatorId: e.target.value }))}
                >
                  <option value="">Nessuno</option>
                  {operators.map(o => (
                    <option key={o.id} value={String(o.id)}>{o.name}</option>
                  ))}
                </Select>
                <Select label="Gara"
                  value={editForm.contestId}
                  onChange={e => setEditForm(f => ({ ...f, contestId: e.target.value }))}
                >
                  <option value="">Nessuna</option>
                  {contests.map(c => (
                    <option key={c.id} value={String(c.id)}>{c.name}</option>
                  ))}
                </Select>
              </FormSection>
              {/* TASK-0186 (REQ-023-002): "Commenti" -- read-and-append only, no edit/delete
                  of existing comments (same append-only principle as the backend). */}
              <FormSection title="Commenti" columns={1}>
                {commentError && <Callout tone="error">{commentError}</Callout>}
                {loadingComments ? (
                  <LoadingState />
                ) : comments.length === 0 ? (
                  <div className="text-muted md-typescale-body-small">Nessun commento</div>
                ) : (
                  <List aria-label="Commenti">
                    {comments.map(c => (
                      <ListItem
                        key={c.id}
                        headline={c.content}
                        supporting={`${c.authorId != null ? `Utente ${c.authorId} — ` : ''}${formatDateTime(c.createdAt)}`}
                      />
                    ))}
                  </List>
                )}
                <div className="d-flex gap-2">
                  <TextField
                    label="Aggiungi un commento..."
                    className="flex-grow-1"
                    labelHidden
                    placeholder="Aggiungi un commento..."
                    value={commentText}
                    onChange={e => setCommentText(e.target.value)}
                    disabled={addingComment}
                  />
                  <Button variant="outlined" size="sm"
                    disabled={!commentText.trim() || addingComment}
                    onClick={() => void handleAddComment()}>
                    {addingComment ? '...' : 'Aggiungi'}
                  </Button>
                </div>
              </FormSection>
              {/* TASK-0219 (REQ-023-002): "Checklist" -- mutable, unlike "Commenti" above:
                  each row can be toggled done/not-done and deleted. */}
              <FormSection title="Checklist" columns={1}>
                {checklistError && (
                  <Callout tone="error">{checklistError}</Callout>
                )}
                {loadingChecklist ? (
                  <LoadingState />
                ) : checklist.length === 0 ? (
                  <div className="text-muted md-typescale-body-small">Nessuna voce</div>
                ) : (
                  <List aria-label="Checklist">
                    {checklist.map(c => (
                      <ListItem
                        key={c.id}
                        headline={
                          <Checkbox
                            label={<span className={c.isDone ? 'text-decoration-line-through text-muted' : ''}>{c.text}</span>}
                            checked={c.isDone}
                            onChange={e => void handleToggleChecklistItem(c.id, e.target.checked)}
                          />
                        }
                        trailing={<IconButton icon="close" aria-label="Elimina voce" size="sm" onClick={() => void handleDeleteChecklistItem(c.id)} />}
                      />
                    ))}
                  </List>
                )}
                <div className="d-flex gap-2">
                  <TextField
                    label="Aggiungi una voce..."
                    className="flex-grow-1"
                    labelHidden
                    placeholder="Aggiungi una voce..."
                    value={checklistText}
                    onChange={e => setChecklistText(e.target.value)}
                    disabled={addingChecklistItem}
                  />
                  <Button variant="outlined" size="sm"
                    disabled={!checklistText.trim() || addingChecklistItem}
                    onClick={() => void handleAddChecklistItem()}>
                    {addingChecklistItem ? '...' : 'Aggiungi'}
                  </Button>
                </div>
              </FormSection>
              {/* TASK-0195 (REQ-023-002): "Allegati" -- reuses documentsApi (entityType='Task')
                  exactly as-is. No delete UI in this task: documentsApi.remove is Admin-only
                  and out of scope for REQ-023-002. */}
              <FormSection title="Allegati" columns={1}>
                {attachmentError && (
                  <Callout tone="error">{attachmentError}</Callout>
                )}
                {loadingAttachments ? (
                  <LoadingState />
                ) : attachments.length === 0 ? (
                  <div className="text-muted md-typescale-body-small">Nessun allegato</div>
                ) : (
                  <List aria-label="Allegati">
                    {attachments.map(a => (
                      <ListItem
                        key={a.id}
                        headline={
                          <a href={documentsApi.contentUrl(a.id)} download={a.name}>
                            {a.name}
                          </a>
                        }
                        supporting={formatDateTime(a.createdAt)}
                      />
                    ))}
                  </List>
                )}
                <label className="md-button md-button--outlined md-button--sm">
                  {uploadingAttachment ? 'Caricamento...' : 'Carica allegato'}
                  <input
                    type="file"
                    className="d-none"
                    disabled={uploadingAttachment}
                    onChange={e => {
                      const file = e.target.files?.[0]
                      if (file) void handleUploadAttachment(file)
                      e.target.value = ''
                    }}
                  />
                </label>
              </FormSection>
            </>
          )}
      </Dialog>
    </Page>
  )
}

// ─── Appointments section ─────────────────────────────────────────────────────

interface ApptForm {
  title: string
  description: string
  startAt: string
  endAt: string
  location: string
  /** TASK-0154 (REQ-079-001): empty string means "no type selected" -- mapped to `undefined`
   * (create) or `null` (update) before hitting the API, never sent as `''`. */
  type: string
  /** TASK-0185 (REQ-078-001): free text, no vocabulary -- same empty-string-means-absent
   * convention as `location` (trimmed, mapped to `undefined`/`null` before hitting the API). */
  sector: string
  /** TASK-0185 (REQ-078-001): see `sector` above. */
  productOfInterest: string
}

const defaultApptForm: ApptForm = {
  title: '', description: '', startAt: '', endAt: '', location: '', type: '', sector: '', productOfInterest: '',
}

/**
 * TASK-0153 (REQ-081-001): the 9 appointment-outcome values, verbatim — must match
 * `AppointmentOutcomes`'s applied vocabulary in `Appointment.cs` exactly (string-for-string, since
 * the backend validates `outcome` against that same set on
 * `POST /api/appointments/{id}/complete` and rejects anything else with a 400). Not derived from an
 * API call — same arrangement as `LOST_REASONS` in `LeadDetailPage.tsx` (TASK-0150): the list is
 * small, static, and this task's `allowed_paths` has no shared-contract file to fetch it from at
 * runtime.
 */
const APPOINTMENT_OUTCOMES = [
  'Contratto concluso',
  'Interessato',
  'Preventivo inviato',
  'Follow-up necessario',
  'Non interessato',
  'Non qualificato',
  'Da ricontattare',
  'Perso',
  'Riprogrammato',
]

/**
 * TASK-0154 (REQ-079-001): the 8 appointment-type values, verbatim — must match
 * `AppointmentTypes`'s applied vocabulary in `Appointment.cs` exactly (string-for-string, since the
 * backend validates a non-null `type` against that same set on both `POST /api/appointments` and
 * `PUT /api/appointments/{id}` and rejects anything else with a 400). Same arrangement as
 * `APPOINTMENT_OUTCOMES` above: not derived from an API call, this task's `allowed_paths` has no
 * shared-contract file to fetch it from at runtime. Unlike `APPOINTMENT_OUTCOMES`, an appointment
 * with no type at all is always valid -- this list only constrains a type that IS present.
 */
const APPOINTMENT_TYPES = [
  'Telefonico',
  'Videochiamata',
  'Presso cliente',
  'Presso ufficio',
  'Presso negozio',
  'Demo',
  'Analisi fattura',
  'Consulenza',
]

// TASK-0076: edit form shape mirrors `ApptForm` exactly (no extra fields like `TaskEditForm`
// needed for tasks) -- `Appointment.UpdateDetails` touches
// title/description/startAt/endAt/location, and (TASK-0154/REQ-079-001) now type too, but it
// never reads or writes `assignedToId`/`contractId`/`leadId`/`customerId`, so there's nothing
// else to round-trip through this form.
type ApptEditForm = ApptForm

const defaultApptEditForm: ApptEditForm = defaultApptForm

function AppointmentsSection() {
  const snackbar = useSnackbar()
  const confirm = useConfirm()
  const prompt = usePrompt()
  const [sort, setSort] = useState(APPTS_DEFAULT_SORT)
  const [appointments, setAppointments] = useState<Appointment[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [activeFilter, setActiveFilter] = useState<{ from: string; to: string }>({ from: '', to: '' })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // TASK-0232 (REQ-084-001): purely client-side view-mode toggle for the section below (List/
  // Calendar) -- same discipline as `taskViewMode` (TASK-0226/0227): default 'list' so nobody who
  // never touches the toggle sees any behavior change, and it never touches `load()`/
  // `fetchAppointments`: it only changes which JSX branch renders `filteredAppts`.
  // REQ-084-001: widened from 'list' | 'calendar' to distinguish Day/Week/Month formats -- the
  // former 'calendar' grid becomes 'month' below, unchanged in behavior. Still purely
  // client-side; still never touches `load()`/`fetchAppointments`.
  const [apptViewMode, setApptViewMode] = useState<'list' | 'day' | 'week' | 'month'>('list')
  // REQ-084-001: the date the Day/Week/Month grids are anchored on. Defaults to today so Month
  // renders exactly as it did before navigation existed (TASK-0232's "current month only").
  // Only the day/week/month branches below read this -- List and `load()`/`fetchAppointments`
  // never do.
  const [anchorDate, setAnchorDate] = useState<Date>(() => new Date())

  const [showApptModal, setShowApptModal] = useState(false)
  const [apptForm, setApptForm] = useState<ApptForm>(defaultApptForm)
  const [savingAppt, setSavingAppt] = useState(false)

  const [showEditApptModal, setShowEditApptModal] = useState(false)
  const [editApptId, setEditApptId] = useState<number | null>(null)
  const [editApptForm, setEditApptForm] = useState<ApptEditForm>(defaultApptEditForm)
  const [loadingEditAppt, setLoadingEditAppt] = useState(false)
  const [savingEditAppt, setSavingEditAppt] = useState(false)
  const [editApptError, setEditApptError] = useState<string | null>(null)

  // TASK-0153 (REQ-081-001): state for the outcome modal that replaced `handleComplete`'s
  // conferma nativa. Same shape as the edit-appointment modal's state above (id + form fields +
  // saving + error), so the two read alike.
  const [completeApptId, setCompleteApptId] = useState<number | null>(null)
  const [completeOutcome, setCompleteOutcome] = useState('')
  const [completeReason, setCompleteReason] = useState('')
  const [completingAppt, setCompletingAppt] = useState(false)
  const [completeError, setCompleteError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const result = await fetchAppointments({ page, sort })
      setAppointments(result.items)
      setTotal(result.total)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore nel caricamento')
    } finally {
      setLoading(false)
    }
  }, [page, sort])

  function handleSort(next: string) { setSort(next); setPage(1) }

  useEffect(() => { void load() }, [load])

  const filteredAppts = appointments.filter(a => {
    const start = new Date(a.startAt)
    if (activeFilter.from && start < new Date(activeFilter.from)) return false
    if (activeFilter.to && start > new Date(activeFilter.to + 'T23:59:59')) return false
    return true
  })

  const totalPages = Math.ceil(total / PAGE_SIZE)

  async function handleCreateAppt() {
    if (!apptForm.title.trim() || !apptForm.startAt || !apptForm.endAt) return
    setSavingAppt(true)
    try {
      const req: AppointmentCreateRequest = {
        title: apptForm.title.trim(),
        startAt: new Date(apptForm.startAt).toISOString(),
        endAt: new Date(apptForm.endAt).toISOString(),
      }
      if (apptForm.description.trim()) req.description = apptForm.description.trim()
      if (apptForm.location.trim()) req.location = apptForm.location.trim()
      if (apptForm.type) req.type = apptForm.type
      // TASK-0185 (REQ-078-001): same conditional-inclusion treatment as location/type above --
      // free text, no vocabulary, omitted entirely (not sent as '') when blank.
      if (apptForm.sector.trim()) req.sector = apptForm.sector.trim()
      if (apptForm.productOfInterest.trim()) req.productOfInterest = apptForm.productOfInterest.trim()
      await appointmentsApi.create(req)
      setShowApptModal(false)
      setApptForm(defaultApptForm)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    } finally {
      setSavingAppt(false)
    }
  }

  // TASK-0076: opens the edit modal pre-filled from `GET /api/appointments/{id}`
  // (`appointmentsApi.get`), NOT from the `appt` row already in `filteredAppts` -- same reason
  // as `TasksSection.openEdit` (TASK-0075): `AppointmentsController.List`'s projection never
  // selects `Description`, so building the form from the row would silently blank the
  // description of every appointment edited from this table.
  async function openEditAppt(id: number) {
    setEditApptId(id)
    setEditApptError(null)
    setShowEditApptModal(true)
    setLoadingEditAppt(true)
    try {
      const detail = await appointmentsApi.get(id)
      setEditApptForm({
        title: detail.title,
        description: detail.description ?? '',
        startAt: toDatetimeLocalValue(detail.startAt),
        endAt: toDatetimeLocalValue(detail.endAt),
        location: detail.location ?? '',
        type: detail.type ?? '',
        sector: detail.sector ?? '',
        productOfInterest: detail.productOfInterest ?? '',
      })
    } catch (e: unknown) {
      setEditApptError(e instanceof Error ? e.message : "Errore nel caricamento dell'appuntamento")
    } finally {
      setLoadingEditAppt(false)
    }
  }

  function closeEditApptModal() {
    setShowEditApptModal(false)
    setEditApptId(null)
    setEditApptForm(defaultApptEditForm)
    setEditApptError(null)
  }

  // TASK-0076: `description`/`location` are ALWAYS included below (never conditionally, unlike
  // `handleCreateAppt` above) because `Appointment.UpdateDetails` overwrites them
  // unconditionally server-side, even with null -- see `AppointmentUpdateRequest`'s doc comment
  // in api/operations.ts. `startAt`/`endAt` reuse the exact same `new Date(...).toISOString()`
  // conversion as `handleCreateAppt` (avoids the Kind=Unspecified crash class already fixed
  // elsewhere this session).
  async function handleSaveEditAppt() {
    if (editApptId === null || !editApptForm.title.trim() || !editApptForm.startAt || !editApptForm.endAt) return
    setSavingEditAppt(true)
    setEditApptError(null)
    try {
      const req: AppointmentUpdateRequest = {
        title: editApptForm.title.trim(),
        description: editApptForm.description.trim() ? editApptForm.description.trim() : null,
        startAt: new Date(editApptForm.startAt).toISOString(),
        endAt: new Date(editApptForm.endAt).toISOString(),
        location: editApptForm.location.trim() ? editApptForm.location.trim() : null,
        // TASK-0154 (REQ-079-001): always included (same discipline as description/location just
        // above), mapped to null for the empty "Nessuna tipologia" selection.
        type: editApptForm.type ? editApptForm.type : null,
        // TASK-0185 (REQ-078-001): same always-included discipline as type/description/location --
        // `Appointment.UpdateDetails` overwrites Sector/ProductOfInterest unconditionally too, so
        // omitting the key would silently blank a value the user never touched.
        sector: editApptForm.sector.trim() ? editApptForm.sector.trim() : null,
        productOfInterest: editApptForm.productOfInterest.trim() ? editApptForm.productOfInterest.trim() : null,
      }
      await appointmentsApi.update(editApptId, req)
      closeEditApptModal()
      void load()
    } catch (e: unknown) {
      setEditApptError(e instanceof Error ? e.message : 'Errore nel salvataggio')
    } finally {
      setSavingEditAppt(false)
    }
  }

  async function handleCancel(id: number) {
    const ok = await confirm({
      title: 'Annullare l’appuntamento',
      message: 'Annullare questo appuntamento?',
      confirmLabel: 'Annulla appuntamento',
      destructive: true,
    })
    if (!ok) return
    try {
      await appointmentsApi.cancel(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0188 (REQ-080-001): same try/catch/alert/`void load()` shape as `handleCancel` above, its
  // direct template -- no confirmation dialog, matching the equally low-stakes/reversible nature of
  // confirming an appointment (unlike `handleCancel`'s destructive-feeling action, which does confirm).
  async function handleConfirm(id: number) {
    try {
      await appointmentsApi.confirm(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0188 (REQ-080-001): same shape as `handleConfirm` immediately above.
  async function handleMarkNoShow(id: number) {
    try {
      await appointmentsApi.markNoShow(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0251 (REQ-080-001): same try/catch/alert/`void load()` shape as `handleConfirm`/
  // `handleMarkNoShow` above -- no confirmation dialog, same low-stakes/reversible reasoning.
  async function handleMarkToConfirm(id: number) {
    try {
      await appointmentsApi.markToConfirm(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0251 (REQ-080-001): same shape as `handleCancel` above (a conferma modale, matching
  // its destructive-feeling action) -- one of the two differentiated cancellation handlers.
  async function handleCancelByCustomer(id: number) {
    const ok = await confirm({
      title: 'Annullare l’appuntamento',
      message: 'Annullare questo appuntamento (cliente)?',
      confirmLabel: 'Annulla appuntamento',
      destructive: true,
    })
    if (!ok) return
    try {
      await appointmentsApi.cancelByCustomer(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0251 (REQ-080-001): see `handleCancelByCustomer` immediately above, the other
  // differentiated cancellation handler.
  async function handleCancelByAgent(id: number) {
    const ok = await confirm({
      title: 'Annullare l’appuntamento',
      message: 'Annullare questo appuntamento (agente)?',
      confirmLabel: 'Annulla appuntamento',
      destructive: true,
    })
    if (!ok) return
    try {
      await appointmentsApi.cancelByAgent(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0251 (REQ-080-001): same shape as `handleMarkToConfirm` above.
  async function handleMarkRescheduled(id: number) {
    try {
      await appointmentsApi.markRescheduled(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0251 (REQ-080-001): same shape as `handleMarkToConfirm` above, the last of the 9 states.
  async function handleMarkToRecall(id: number) {
    try {
      await appointmentsApi.markToRecall(id)
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  // TASK-0153 (REQ-081-001): the outcome is mandatory server-side, so a plain
  // conferma nativa ('Completare questo appuntamento?') can no longer produce a valid request —
  // it has nothing to say about HOW the appointment went. Opening a modal instead; the actual
  // call moved to `handleConfirmComplete` below.
  function openCompleteModal(id: number) {
    setCompleteApptId(id)
    setCompleteOutcome('')
    setCompleteReason('')
    setCompleteError(null)
  }

  function closeCompleteModal() {
    setCompleteApptId(null)
    setCompleteOutcome('')
    setCompleteReason('')
    setCompleteError(null)
  }

  // The `outcome` sent here is one of `APPOINTMENT_OUTCOMES` verbatim (the <select> offers nothing
  // else and the confirm button stays disabled until one is picked), so the backend's vocabulary
  // check should never fire from this UI. It is still surfaced inline rather than swallowed: a
  // drift between the two copies of the vocabulary has to be visible, not silent.
  async function handleConfirmComplete() {
    if (completeApptId === null || !completeOutcome) return
    setCompletingAppt(true)
    setCompleteError(null)
    try {
      const reason = completeReason.trim()
      await appointmentsApi.complete(completeApptId, completeOutcome, reason ? reason : undefined)
      closeCompleteModal()
      void load()
    } catch (e: unknown) {
      setCompleteError(e instanceof Error ? e.message : 'Errore')
    } finally {
      setCompletingAppt(false)
    }
  }

  async function handleDelete(id: number) {
    // F-104 (REQ-287-001): non si cancella, si sposta nel cestino (si ripristina da Impostazioni > Cestino).
    const reason = await prompt({
      title: 'Sposta l\'appuntamento nel cestino',
      message: 'Spostare questo appuntamento nel cestino? Motivo (facoltativo, max 500 caratteri).',
      label: 'Motivo (facoltativo)',
      initialValue: '',
      confirmLabel: 'Sposta nel cestino',
      destructive: true,
    })
    if (reason === null) return
    try {
      await appointmentsApi.delete(id, reason.slice(0, 500))
      void load()
    } catch (e: unknown) {
      snackbar.show({ message: e instanceof Error ? e.message : 'Errore', tone: 'error' })
    }
  }

  const apptFormValid =
    apptForm.title.trim().length > 0 && apptForm.startAt.length > 0 && apptForm.endAt.length > 0

  // REQ-084-001: moves `anchorDate` by one Day/Week/Month unit depending on `apptViewMode` --
  // the Day/Week/Month nav buttons below are the only callers. No-op (never called) while
  // `apptViewMode === 'list'`, since those buttons only render for the other three modes.
  function shiftAnchor(direction: 1 | -1) {
    setAnchorDate(prev => {
      const next = new Date(prev)
      if (apptViewMode === 'day') next.setDate(next.getDate() + direction)
      else if (apptViewMode === 'week') next.setDate(next.getDate() + direction * 7)
      else {
        // Normalise to the 1st BEFORE shifting the month. setMonth() keeps the current
        // day-of-month, so from the 31st it asks for a date that may not exist -- 31 Aug + 1
        // becomes "31 Sep", which JavaScript silently rolls forward to 1 Oct, skipping
        // September entirely. anchorDate is seeded from `new Date()`, so this fired for real on
        // any 29th/30th/31st. The Month grid reads only the anchor's year and month (see
        // monthMatrix below), so pinning the day to 1 changes nothing else.
        next.setDate(1)
        next.setMonth(next.getMonth() + direction)
      }
      return next
    })
  }

  // REQ-084-001: appointments on one local day -- the exact filter TASK-0232's Month grid
  // already used, factored out here so Day/Week/Month share one implementation.
  function apptsOnDate(date: Date) {
    return filteredAppts.filter(a => {
      const d = new Date(a.startAt)
      return d.getFullYear() === date.getFullYear() && d.getMonth() === date.getMonth() && d.getDate() === date.getDate()
    })
  }

  // REQ-084-001: one day's badge list -- identical markup to TASK-0232's Month-grid cell body,
  // shared by Day/Week/Month so all three render appointments alike. Reuses
  // `apptStatusColors`/`openEditAppt` -- no new lookups/actions.
  function renderApptBadges(date: Date) {
    return (
      <div className="d-flex flex-column gap-1">
        {apptsOnDate(date).map(appt => {
          const statusColor = apptStatusColors[appt.status] ?? 'secondary'
          return (
            <button
              key={appt.id}
              type="button"
              title={appt.title}
              className="border-0 bg-transparent p-0 text-start w-100"
              onClick={() => void openEditAppt(appt.id)}
              >
                <StatusPill tone={pillTone(statusColor)} className="text-truncate">{appt.title}</StatusPill>
              </button>
          )
        })}
      </div>
    )
  }

  return (
    <Page>
      {/* Filter row */}
      <div className="d-flex gap-2 justify-content-between mb-3 flex-wrap align-items-center">
        <div className="d-flex gap-2 align-items-center flex-wrap">
          <TextField
            label="Dal"
            labelHidden
            className="w-auto"
            type="date"
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <TextField
            label="Al"
            labelHidden
            className="w-auto"
            type="date"
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
          <Button variant="outlined" size="sm"
            onClick={() => setActiveFilter({ from: dateFrom, to: dateTo })}>
            Filtra
          </Button>
          <SortResetLink sort={sort} defaultSort={APPTS_DEFAULT_SORT} onReset={() => handleSort(APPTS_DEFAULT_SORT)} />
          {/* TASK-0232 (REQ-084-001): List/Calendar view toggle -- purely client-side, same
              pattern ButtonGroup/onClick as `taskViewMode`'s toggle above (TASK-0226/0227),
              does not touch any filter state or trigger `load()`.
              REQ-084-001: extended from List/Calendario to List/Giorno/Settimana/Mese -- same
              idioma ButtonGroup, one more button per added format. */}
          <SegmentedButton
            aria-label="Modalità di visualizzazione"
            options={[{ value: 'list', label: 'Lista' }, { value: 'day', label: 'Giorno' }, { value: 'week', label: 'Settimana' }, { value: 'month', label: 'Mese' }]}
            value={apptViewMode}
            onChange={v => setApptViewMode(v as 'list' | 'day' | 'week' | 'month')}
          />
          {/* REQ-084-001: prev/today/next navigation for the anchored Day/Week/Month grids --
              hidden in List (which has no `anchorDate` concept), same IconButton
              + material-symbols chevron idiom as the page controls elsewhere in this app
              (e.g. TeamPage.tsx's "Precedente"/"Successiva"). */}
          {apptViewMode !== 'list' && (
            <div className="d-flex gap-1 align-items-center" role="group" aria-label="Navigazione data">
              <IconButton icon="chevron_left" aria-label="Precedente" size="sm" onClick={() => shiftAnchor(-1)} />
              <Button variant="outlined" size="sm" onClick={() => setAnchorDate(new Date())}>
                Oggi
              </Button>
              <IconButton icon="chevron_right" aria-label="Successivo" size="sm" onClick={() => shiftAnchor(1)} />
            </div>
          )}
        </div>
        <Button variant="filled" size="sm" icon="add"
          onClick={() => setShowApptModal(true)}>
          Nuovo appuntamento
        </Button>
      </div>

      {error && <Callout tone="error">{error}</Callout>}

      {/* Table card */}
      <Card padding="none">
          {loading ? (
            <LoadingState />
          ) : filteredAppts.length === 0 ? (
            <EmptyState icon="event" title="Nessun appuntamento" />
          ) : apptViewMode === 'month' ? (
            // TASK-0232 (REQ-084-001): Calendar view -- read-only monthly grid, grouped
            // client-side by local year/month/day of `startAt`. Reuses `apptStatusColors`/
            // `openEditAppt` -- no new lookups/actions.
            // REQ-084-001: now anchored on `anchorDate` (navigable via the prev/Today/next
            // buttons above) instead of hardcoded to the render-time current month, and the
            // per-day badge list is factored into `renderApptBadges`, shared with Day/Week below.
            (() => {
              const year = anchorDate.getFullYear()
              const month = anchorDate.getMonth()
              const daysInMonth = new Date(year, month + 1, 0).getDate()
              // getDay(): 0=Sunday..6=Saturday -- shifted to a Monday-first week (it-IT
              // convention); purely a presentation choice, not mandated by the task card.
              const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7
              const cells: Array<number | null> = [
                ...Array.from({ length: firstWeekday }, () => null),
                ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
              ]
              while (cells.length % 7 !== 0) cells.push(null)
              const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
              return (
                <div className="p-3">
                  <div className="mb-2 md-typescale-emphasized-body-medium">
                    {anchorDate.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className="md-u-scroll-x">
                    <div className="d-grid md-u-grid-7">
                      {weekdayLabels.map(label => (
                        <div key={label} className="text-center text-muted md-typescale-emphasized-body-small">
                          {label}
                        </div>
                      ))}
                      {cells.map((day, idx) => {
                        if (day === null) {
                          return <div key={`empty-${idx}`} className="md-u-minh-88" />
                        }
                        return (
                          <Card as="div" variant="outlined" padding="sm" key={day} className="gap-1 md-u-minh-88">
                            <div className="md-typescale-body-small text-muted mb-1">{day}</div>
                            {renderApptBadges(new Date(year, month, day))}
                          </Card>
                        )
                      })}
                    </div>
                  </div>
                </div>
              )
            })()
          ) : apptViewMode === 'week' ? (
            // REQ-084-001: Week format -- 7 day-columns spanning the Monday..Sunday week that
            // contains `anchorDate`, reusing the exact same per-day badge list as Month
            // (`renderApptBadges`) and the same Monday-first weekday labels.
            (() => {
              const weekStart = startOfWeekMonday(anchorDate)
              const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom']
              const days = Array.from({ length: 7 }, (_, i) => {
                const d = new Date(weekStart)
                d.setDate(d.getDate() + i)
                return d
              })
              const weekEnd = days[6]
              return (
                <div className="p-3">
                  <div className="mb-2 md-typescale-emphasized-body-medium">
                    {weekStart.toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                    {' – '}
                    {weekEnd.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </div>
                  <div className="md-u-scroll-x">
                    <div className="d-grid md-u-grid-7">
                      {days.map(d => (
                        <div key={`label-${d.toISOString()}`} className="text-center text-muted md-typescale-emphasized-body-small">
                          {weekdayLabels[(d.getDay() + 6) % 7]} {d.getDate()}
                        </div>
                      ))}
                      {days.map(d => (
                        <Card as="div" variant="outlined" padding="sm" key={`cell-${d.toISOString()}`} className="md-u-minh-200">
                          {renderApptBadges(d)}
                        </Card>
                      ))}
                    </div>
                  </div>
                </div>
              )
            })()
          ) : apptViewMode === 'day' ? (
            // REQ-084-001: Day format -- a single full-width column for `anchorDate`, same
            // per-day badge list as Month/Week (`renderApptBadges`).
            <div className="p-3">
              <div className="mb-2 md-typescale-emphasized-body-medium">
                {anchorDate.toLocaleDateString('it-IT', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </div>
              <Card as="div" variant="outlined" padding="sm" className="md-u-minh-200">
                {renderApptBadges(anchorDate)}
              </Card>
            </div>
          ) : (
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <SortKeyTh label="Data/Ora" ascKey="start_asc" descKey="start_desc" sort={sort} onSort={handleSort} />
                    <th>Titolo</th>
                    <th>Luogo</th>
                    <th>Tipo</th>
                    <SortKeyTh label="Stato" ascKey="status" sort={sort} onSort={handleSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAppts.map(appt => {
                    const statusColor = apptStatusColors[appt.status] ?? 'secondary'
                    const statusLabel = apptStatusLabels[appt.status] ?? appt.status
                    return (
                      <tr key={appt.id}>
                        <td>
                          <strong className="md-typescale-body-small">{formatDateTime(appt.startAt)}</strong>
                        </td>
                        <td>{appt.title}</td>
                        <td><small>{appt.location ?? '-'}</small></td>
                        <td><small>{appt.type ?? 'Non specificato'}</small></td>
                        <td>
                          <StatusPill tone={pillTone(statusColor)}>{statusLabel}</StatusPill>
                        </td>
                        <td className="text-end">
                          <div className="d-flex justify-content-end flex-wrap gap-1">
                          <IconButton size="sm" icon="edit" aria-label="Modifica" onClick={() => void openEditAppt(appt.id)} />
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                            <IconButton size="sm" icon="done" aria-label="Completa" onClick={() => openCompleteModal(appt.id)} />
                          )}
                          {/* TASK-0188 (REQ-080-001): same base visibility guard as "Completa"/
                              "Annulla" (not cancelled, not completed -- confirming/marking a closed
                              appointment makes no sense), TIGHTENED with one more exclusion each:
                              hide "Conferma" once the appointment is already `confirmed`, and hide
                              "Non presentato" once it is already `no_show` -- showing a button whose
                              only effect is to reapply the status the row already shows would just be
                              noise, following the exact same "don't show the action for the state
                              you're already in" pattern the base guard already applies to `cancelled`/
                              `completed`. This is a pure UI/UX narrowing: the backend transition
                              itself stays unconditional (see Appointment.Confirm/MarkNoShow's doc
                              comments) -- a user could still reach either endpoint by other means and
                              it would still succeed. */}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'confirmed' && (
                            <IconButton size="sm" icon="event_available" aria-label="Conferma" onClick={() => void handleConfirm(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'no_show' && (
                            <IconButton size="sm" icon="event_busy" aria-label="Non presentato" onClick={() => void handleMarkNoShow(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && (
                            <IconButton size="sm" icon="cancel" aria-label="Annulla" onClick={() => void handleCancel(appt.id)} />
                          )}
                          {/* TASK-0251 (REQ-080-001): the remaining 4 states of the requirement's 9-state
                              vocabulary. Same guard shape as "Conferma"/"Non presentato" above: the base
                              "not cancelled, not completed" guard plus, for each button, hiding it once
                              the appointment is already in that specific new status -- the same
                              "don't show the action for the state you're already in" pattern, and the
                              same pure UI/UX narrowing (the backend transitions themselves stay
                              unconditional, see the corresponding Appointment methods' doc comments). */}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'to_confirm' && (
                            <IconButton size="sm" icon="pending" aria-label="Da confermare" onClick={() => void handleMarkToConfirm(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'cancelled_by_customer' && (
                            <IconButton size="sm" icon="person_off" aria-label="Annullato cliente" onClick={() => void handleCancelByCustomer(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'cancelled_by_agent' && (
                            <IconButton size="sm" icon="block" aria-label="Annullato agente" onClick={() => void handleCancelByAgent(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'rescheduled' && (
                            <IconButton size="sm" icon="event_repeat" aria-label="Rinviato" onClick={() => void handleMarkRescheduled(appt.id)} />
                          )}
                          {appt.status !== 'cancelled' && appt.status !== 'completed' && appt.status !== 'to_recall' && (
                            <IconButton size="sm" icon="phone_callback" aria-label="Da richiamare" onClick={() => void handleMarkToRecall(appt.id)} />
                          )}
                          <IconButton size="sm" icon="delete" aria-label="Sposta nel cestino" onClick={() => void handleDelete(appt.id)} />
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </DataTable>
          )}
      </Card>

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center md-typescale-body-small text-muted">
        <span>{total.toLocaleString('it-IT')} totali</span>
        <div className="d-flex gap-2 align-items-center">
          <Button variant="outlined" size="sm"
            disabled={page === 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}>
            ‹ Precedente
          </Button>
          <span>Pagina {page}{totalPages > 0 ? ` di ${totalPages}` : ''}</span>
          <Button variant="outlined" size="sm"
            disabled={appointments.length < PAGE_SIZE}
            onClick={() => setPage(p => p + 1)}>
            Successiva ›
          </Button>
        </div>
      </div>

      {/* New Appointment Modal */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleCreateAppt() }}
        open={showApptModal}
        onClose={() => setShowApptModal(false)}
        title="Nuovo appuntamento"
        actions={
          <>
            <Button variant="text"
              onClick={() => setShowApptModal(false)}>
              Annulla
            </Button>
            <Button type="submit" variant="text"
              disabled={!apptFormValid || savingAppt}
              >
              {savingAppt ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
          <FormSection title="Appuntamento">
            <TextField className="md-span-full" label="Titolo *"
              type="text"
              required
              value={apptForm.title}
              onChange={e => setApptForm(f => ({ ...f, title: e.target.value }))}
            />
            <Select label="Tipo"
              id="appt-type"
              value={apptForm.type}
              onChange={e => setApptForm(f => ({ ...f, type: e.target.value }))}
            >
              <option value="">Nessuna tipologia</option>
              {APPOINTMENT_TYPES.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
            <TextField label="Luogo"
              type="text"
              value={apptForm.location}
              onChange={e => setApptForm(f => ({ ...f, location: e.target.value }))}
            />
          </FormSection>
          <FormSection title="Quando">
            <TextField label="Inizio *"
              type="datetime-local"
              value={apptForm.startAt}
              onChange={e => setApptForm(f => ({ ...f, startAt: e.target.value }))}
            />
            <TextField label="Fine *"
              type="datetime-local"
              value={apptForm.endAt}
              onChange={e => setApptForm(f => ({ ...f, endAt: e.target.value }))}
            />
          </FormSection>
          {/* TASK-0185 (REQ-078-001): free-text inputs, not <select> -- Sector/ProductOfInterest
              have no vocabulary, unlike Tipo above. */}
          <FormSection title="Con chi">
            <TextField label="Settore"
              id="appt-sector"
              type="text"
              value={apptForm.sector}
              onChange={e => setApptForm(f => ({ ...f, sector: e.target.value }))}
            />
            <TextField label="Prodotto di interesse"
              id="appt-product-of-interest"
              type="text"
              value={apptForm.productOfInterest}
              onChange={e => setApptForm(f => ({ ...f, productOfInterest: e.target.value }))}
            />
          </FormSection>
          <FormSection title="Note" columns={1}>
            <TextField multiline label="Descrizione"
              rows={2}
              value={apptForm.description}
              onChange={e => setApptForm(f => ({ ...f, description: e.target.value }))}
            />
          </FormSection>
      </Dialog>

      {/* Edit Appointment Modal (TASK-0076) */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleSaveEditAppt() }}
        open={showEditApptModal}
        onClose={closeEditApptModal}
        title="Modifica appuntamento"
        actions={
          <>
            <Button variant="text" onClick={closeEditApptModal}>
              Annulla
            </Button>
            <Button type="submit" variant="text"
              disabled={
                loadingEditAppt ||
                !editApptForm.title.trim() ||
                !editApptForm.startAt ||
                !editApptForm.endAt ||
                savingEditAppt
              }
              >
              {savingEditAppt ? 'Salvataggio...' : 'Salva'}
            </Button>
          </>
        }
      >
          {loadingEditAppt ? (
            <LoadingState />
          ) : (
            <>
              {editApptError && <Callout tone="error">{editApptError}</Callout>}
              <FormSection title="Appuntamento">
                <TextField className="md-span-full" label="Titolo *"
                  type="text"
                  required
                  value={editApptForm.title}
                  onChange={e => setEditApptForm(f => ({ ...f, title: e.target.value }))}
                />
                <Select label="Tipo"
                  id="edit-appt-type"
                  value={editApptForm.type}
                  onChange={e => setEditApptForm(f => ({ ...f, type: e.target.value }))}
                >
                  <option value="">Nessuna tipologia</option>
                  {APPOINTMENT_TYPES.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </Select>
                <TextField label="Luogo"
                  type="text"
                  value={editApptForm.location}
                  onChange={e => setEditApptForm(f => ({ ...f, location: e.target.value }))}
                />
              </FormSection>
              <FormSection title="Quando">
                <TextField label="Inizio *"
                  type="datetime-local"
                  value={editApptForm.startAt}
                  onChange={e => setEditApptForm(f => ({ ...f, startAt: e.target.value }))}
                />
                <TextField label="Fine *"
                  type="datetime-local"
                  value={editApptForm.endAt}
                  onChange={e => setEditApptForm(f => ({ ...f, endAt: e.target.value }))}
                />
              </FormSection>
              {/* TASK-0185 (REQ-078-001): free-text inputs, not <select> -- same reasoning as
                  the create modal above. */}
              <FormSection title="Con chi">
                <TextField label="Settore"
                  id="edit-appt-sector"
                  type="text"
                  value={editApptForm.sector}
                  onChange={e => setEditApptForm(f => ({ ...f, sector: e.target.value }))}
                />
                <TextField label="Prodotto di interesse"
                  id="edit-appt-product-of-interest"
                  type="text"
                  value={editApptForm.productOfInterest}
                  onChange={e => setEditApptForm(f => ({ ...f, productOfInterest: e.target.value }))}
                />
              </FormSection>
              <FormSection title="Note" columns={1}>
                <TextField multiline label="Descrizione"
                  rows={2}
                  value={editApptForm.description}
                  onChange={e => setEditApptForm(f => ({ ...f, description: e.target.value }))}
                />
              </FormSection>
            </>
          )}
      </Dialog>

      {/* Complete Appointment Modal (TASK-0153 / REQ-081-001) — replaces the
          la conferma nativa 'Completare questo appuntamento?' che questa sezione mostrava, which had
          no way to carry the now-mandatory outcome. Same chrome as the "Modifica Appuntamento"
          modal above (modal / modal-dialog / modal-content / header-body-footer, `d-block` with the
          inline backdrop), reused rather than reinvented. */}
      <Dialog
        as="form"
        onSubmit={(e) => { e.preventDefault(); void handleConfirmComplete() }}
        open={completeApptId !== null}
        onClose={closeCompleteModal}
        title="Completa appuntamento"
        supportingText="Seleziona l'esito di questo appuntamento."
        dismissible={!completingAppt}
        actions={
          <>
            <Button variant="text"
              onClick={closeCompleteModal}
              disabled={completingAppt}>
              Annulla
            </Button>
            <Button type="submit" variant="text"
              disabled={!completeOutcome || completingAppt}
              >
              {completingAppt ? 'Salvataggio...' : 'Conferma'}
            </Button>
          </>
        }
      >
          {completeError && <Callout tone="error">{completeError}</Callout>}
          <FormGrid columns={1}>
            <Select label="Esito *"
              id="appt-outcome"
              value={completeOutcome}
              onChange={e => setCompleteOutcome(e.target.value)}
            >
              <option value="">Seleziona un esito...</option>
              {APPOINTMENT_OUTCOMES.map(o => (
                <option key={o} value={o}>{o}</option>
              ))}
            </Select>
            <TextField multiline label="Motivo (facoltativo)"
              id="appt-outcome-reason"
              rows={2}
              value={completeReason}
              onChange={e => setCompleteReason(e.target.value)}
            />
          </FormGrid>
      </Dialog>
    </Page>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function OperationsPage() {
  // TASK-0062: initialize from the sidebar's `?tab=` query param (App.tsx's Task/Appuntamenti
  // NavItems) instead of always defaulting to 'tasks', so clicking "Appuntamenti" lands directly
  // on the Appointments tab. Same `useSearchParams().get(...)` pattern already used by
  // ContractFormPage.tsx/DocumentsPage.tsx. Read once into initial state and not synced back
  // when the user clicks the in-page tabs below — those already work via local state today, and
  // this task's scope is wiring the initial nav landing, not redesigning the page's URL sync.
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<ActiveTab>(() =>
    searchParams.get('tab') === 'appointments' ? 'appointments' : 'tasks'
  )

  // TASK-0062 fix (found during independent review, confirmed with real Chrome): React Router
  // does not remount OperationsPage when only the search string changes (same route
  // `/operations`, different `?tab=`), so the lazy useState initializer above only ever fires on
  // the FIRST mount. Clicking the sidebar's "Task"/"Appuntamenti" NavItem while already sitting
  // on `/operations` updated the URL and the nav highlight (App.tsx's NavItem is driven by the
  // live `useLocation()`) but left the rendered tab unchanged -- e.g. from Appointments, clicking
  // "Task" moved the URL to `?tab=tasks` and lit up the Task nav entry, while the page kept
  // showing "Nuovo Appuntamento" instead of "Nuovo Task". This effect re-syncs `activeTab`
  // whenever the URL's `tab` changes to a recognized value, without interfering with the in-page
  // tab buttons below (they call `setActiveTab` directly and never touch the query string).
  useEffect(() => {
    const tab = searchParams.get('tab')
    if (tab === 'appointments' || tab === 'tasks') {
      setActiveTab(tab)
    }
  }, [searchParams])

  return (
    <Page>
      <PageHeader title={activeTab === 'tasks' ? 'Task' : 'Appuntamenti'} icon={activeTab === 'tasks' ? 'task_alt' : 'calendar_month'} />
      {/* Tabs */}
      <Tabs
        aria-label="Sezioni operative"
        items={[
          { id: 'tasks', label: 'Task', icon: 'task_alt' },
          { id: 'appointments', label: 'Appuntamenti', icon: 'calendar_month' },
        ]}
        value={activeTab}
        onChange={id => setActiveTab(id as ActiveTab)}
      />

      {activeTab === 'tasks' && <TasksSection />}
      {activeTab === 'appointments' && <AppointmentsSection />}
    </Page>
  )
}
